/**
 * Tooltip, Dialog and Menu.
 *
 * All three are Radix primitives with Exi Git's tokens on top, which is exactly
 * the split ADR-0003 chose: *"Radix supplies unstyled, tested behaviour;
 * DESIGN_LANGUAGE.md supplies every visual decision. We buy the part that is
 * hard to get right and keep the part that is ours."*
 *
 * The part that is hard to get right, concretely: focus trapping and restoring,
 * `aria-expanded`/`aria-controls` wiring, Escape and outside-click dismissal,
 * typeahead in menus, roving tabindex, scroll locking without layout shift,
 * and the pointer-versus-keyboard distinction in hover cards. Every one of
 * those is a WCAG failure when hand-rolled, and none of them is visible in a
 * screenshot.
 */

import { Dialog as RadixDialog, DropdownMenu, Tooltip as RadixTooltip } from 'radix-ui';
import type { ReactNode } from 'react';

import { cx } from '~/lib/cx';
import { IconButton } from './Button';
import { Check, Close } from './icons';

import styles from './Overlays.module.css';

/* ====================================================================== *
 * Tooltip
 * ====================================================================== */

/**
 * Mount once, near the root.
 *
 * `delayDuration` is 400ms rather than Radix's 700: §1 asks the interface to be
 * optimized for the tenth hour, and 700ms is long enough that an experienced
 * user has already moved on. `skipDelayDuration` lets a user scanning a toolbar
 * read the second and third tooltips immediately.
 */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <RadixTooltip.Provider delayDuration={400} skipDelayDuration={200}>
      {children}
    </RadixTooltip.Provider>
  );
}

export interface TooltipProps {
  /**
   * The tooltip text.
   *
   * §1 anti-principles: *"tooltips as the only carrier of essential
   * information"*. This is for a shortcut hint, a full timestamp, a truncated
   * name — never for something the user must know to proceed, because a
   * tooltip is unreachable on touch and easy to miss with a screen reader.
   */
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  children: ReactNode;
}

export function Tooltip({ content, side = 'top', children }: TooltipProps) {
  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content className={styles.tooltip} side={side} sideOffset={6}>
          {content}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

/* ====================================================================== *
 * Dialog
 * ====================================================================== */

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /**
   * A sentence under the title.
   *
   * Strongly encouraged rather than required: Radix wires this to
   * `aria-describedby`, and a dialog that asks a question with no description
   * leaves a screen-reader user with a title and two buttons.
   */
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** `sm` for a confirmation, `md` for a form. */
  size?: 'sm' | 'md';
}

/**
 * A modal dialog.
 *
 * §1 anti-principles: *"modal dialogs for anything reversible"*. A dialog is
 * for a decision that cannot be undone, or for a form that must not lose its
 * context. Anything else is a page, a popover or an inline edit.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'sm',
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.backdrop} />
        <RadixDialog.Content
          className={cx(styles.dialog, styles[size])}
          onEscapeKeyDown={(event) => {
            // A combobox inside the dialog owns the first Escape while its
            // list is open: that keystroke closes the list, the next one the
            // dialog. Radix hears the key at the document before the input
            // does, so the check is made here from the list's own flag.
            const target = event.target as HTMLElement | null;
            if (target?.closest('[data-combobox-open="true"]')) event.preventDefault();
          }}
        >
          <div className={styles.dialogHeader}>
            <RadixDialog.Title className={styles.dialogTitle}>{title}</RadixDialog.Title>
            <RadixDialog.Close asChild>
              <IconButton label="Close" icon={<Close />} size="sm" />
            </RadixDialog.Close>
          </div>

          {description ? (
            <RadixDialog.Description className={styles.dialogDescription}>
              {description}
            </RadixDialog.Description>
          ) : null}

          {children ? <div className={styles.dialogBody}>{children}</div> : null}
          {footer ? <div className={styles.dialogFooter}>{footer}</div> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/* ====================================================================== *
 * Menu
 * ====================================================================== */

export interface MenuProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export function Menu({ trigger, children, align = 'end', side = 'bottom' }: MenuProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={styles.menu}
          align={align}
          side={side}
          sideOffset={4}
          collisionPadding={8}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export interface MenuItemProps {
  onSelect?: () => void;
  icon?: ReactNode;
  /** A keyboard hint at the trailing edge. Display only; bind the key yourself. */
  shortcut?: string;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  children: ReactNode;
}

export function MenuItem({
  onSelect,
  icon,
  shortcut,
  disabled = false,
  tone = 'default',
  children,
}: MenuItemProps) {
  return (
    <DropdownMenu.Item
      className={cx(styles.menuItem, tone === 'danger' && styles.menuItemDanger)}
      disabled={disabled}
      {...(onSelect ? { onSelect } : {})}
    >
      {icon ? <span className={styles.menuItemIcon}>{icon}</span> : null}
      <span className={styles.menuItemLabel}>{children}</span>
      {shortcut ? <kbd className={styles.menuItemShortcut}>{shortcut}</kbd> : null}
    </DropdownMenu.Item>
  );
}

export interface MenuRadioGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  options: { value: string; label: string; icon?: ReactNode }[];
}

export function MenuRadioGroup({ value, onValueChange, label, options }: MenuRadioGroupProps) {
  return (
    <>
      <DropdownMenu.Label className={styles.menuLabel}>{label}</DropdownMenu.Label>
      <DropdownMenu.RadioGroup value={value} onValueChange={onValueChange}>
        {options.map((option) => (
          <DropdownMenu.RadioItem
            key={option.value}
            value={option.value}
            className={styles.menuItem}
          >
            <span className={styles.menuItemIcon}>
              {/* The check replaces the icon when selected rather than sitting
               * beside it: two indicators in one row is one more than the row
               * can carry at this density. */}
              <DropdownMenu.ItemIndicator>
                <Check size={14} />
              </DropdownMenu.ItemIndicator>
              <span className={styles.menuItemIconIdle}>{option.icon}</span>
            </span>
            <span className={styles.menuItemLabel}>{option.label}</span>
          </DropdownMenu.RadioItem>
        ))}
      </DropdownMenu.RadioGroup>
    </>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className={styles.menuSeparator} />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DropdownMenu.Label className={styles.menuLabel}>{children}</DropdownMenu.Label>;
}
